/**************************************** PROJECT MODULES *****************************************/
import {
    GenericConceptInstance,
    LocalDate,
    PHUIntegrationInstance,
    RecordInstance,
} from '@canimmunize/javelinscript';

import {RootStore} from '../models/root-store';

/********************************************* EXPORT *********************************************/
/**
 * Determine if string extracted from key on a GenericConcept object is valid
 * @param {string} str String extracted from key on a GenericConcept object
 * @return {boolean} True if string extract from a key on a GenericConcept is valid
 */
export const isConceptStrValid = (str: string) => !!(str && str !== `###Error###` && str !== `???`);

/**
 * Extract abbreviation from GenericConcept
 * If not found, create rough abbreviation by generating GenericConcept's
 * section title & grabbing the 1st word from it
 */
export const getAbbreviationFromConcept = (concept: GenericConceptInstance): string => {
    // Log error if concept intended for picklist display but abbreviation isn't present
    if (concept.isShownInPickList !== false && !isConceptStrValid(concept.abbreviation)) {
        console.error(
            `[ERROR] getAbbreviationFromConcept :: no abbreviation for tradeNameConcept concept:`,
            concept
        );
    }

    // Return abbreviation (or ###Error### if it's not present)
    return concept.abbreviation;
};

/**
 * Generate section title from GenericConcept by grabbing concept.displayTerm
 * Returns ###Error### if it fails
 */
export const getSectionTitleFromConcept = (opt: GenericConceptInstance, srcFn?: string): string => {
    if (isConceptStrValid(opt.displayTerm)) return opt.displayTerm;

    const srcFnStr = ((srcFn && `{{` + srcFn + `}} --> `) || ``) + `[ERROR]`;
    console.error(`${srcFnStr} getSectionTitleFromConcept :: no val for genericConcept opt:`, opt);

    return opt.displayTerm;
};

/**
 * @return {boolean} True if LocalDate values are equal
 */
export const areLocalDatesEqual = <D1 extends LocalDate, D2 extends LocalDate>(
    d1: D1,
    d2: D2
): boolean => {
    if (!d1 && !d2) return true;
    if ((!d1 && d2) || (d1 && !d2)) return false;
    return d1.isEqual(d2);
};

/**
 * From [appState] store, get PHU integration of provided [record], or if record
 * not provided, defaults to getting currently active record's PHU integration
 *
 * @param {Store} root Full app data store
 * @param {RecordInstance} record Record to get PHU integration for
 * @param {boolean} allowMinimallyEligible If true, still return PHU integration
 *                  if record is minimally eligible (i.e. right region & age)
 *
 * @return {PHUIntegration|null} If found, return PHUIntegration for the record
 *                               If none present, return null
 */
export const getPHUIntegration = (
    appState: RootStore,
    record?: RecordInstance,
    allowMinimallyEligible: boolean = false
): PHUIntegrationInstance | null => {
    const cleanRecord = record || appState.activeRecordModel.record;

    for (const phuConfig of appState.javelinDataStore.phuConfigs) {
        // Don't show a phu if it is not enabled
        if (phuConfig.isEnabled === false) continue;

        const curPHUIntegration = cleanRecord.getPHUIntegration(phuConfig.phuId);
        if (!curPHUIntegration) continue;

        if (allowMinimallyEligible && !curPHUIntegration.isNew) return curPHUIntegration;

        const isMinimallyEligible = !curPHUIntegration.checkMinimumEligibility();
        if (isMinimallyEligible) return curPHUIntegration;
    }

    return null;
};

/********************************************* CONFIG *********************************************/
export const statusCodes = {
    '0': `overdue`,
    '1': `approaching`,
    '2': `received`,
    '3': `future`,
    '4': `ignored`,
};

export const isNotificationReceived = (notification: {status: number}) =>
    !!(statusCodes[notification.status] === `received`);
